import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  ChartLine, Users, Kanban, ShoppingCart, CurrencyInr, Bell, TrendUp
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line,
} from "recharts";
import { useAuth } from "@/context/AuthContext";

const Kpi = ({ label, value, icon: Icon, hint, testId }) => (
  <div className="p-5 border-r border-b border-border/60 bg-card hover-lift animate-fade-in" data-testid={testId}>
    <div className="flex items-start justify-between mb-3">
      <div className="overline text-muted-foreground">{label}</div>
      <Icon size={18} weight="duotone" className="text-primary" />
    </div>
    <div className="font-heading text-3xl font-bold tracking-tight leading-none">{value}</div>
    {hint && <div className="text-xs text-muted-foreground mt-2">{hint}</div>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [perf, setPerf] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/dashboard/employee-performance").then((r) => setPerf(r.data));
  }, []);

  const fmt = (n) => `₹${(n ?? 0).toLocaleString("en-IN")}`;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline text-muted-foreground mb-2">Overview</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">
            Namaste, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-sm text-muted-foreground mt-2">Here's what's moving in your workshop today.</p>
        </div>
        <div className="text-xs text-muted-foreground">{new Date().toLocaleDateString("en-IN", { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-6 border-l border-t border-border/60 rounded-lg overflow-hidden">
        <Kpi label="Total Leads" value={stats?.total_leads ?? 0} icon={Kanban} testId="kpi-total-leads" />
        <Kpi label="New Today" value={stats?.new_today ?? 0} icon={TrendUp} hint="Fresh enquiries" testId="kpi-new-today" />
        <Kpi label="Follow-ups Due" value={stats?.followup_today ?? 0} icon={Bell} hint="Today" testId="kpi-followup" />
        <Kpi label="Today's Sales" value={fmt(stats?.today_sales)} icon={CurrencyInr} testId="kpi-today-sales" />
        <Kpi label="Pending Payments" value={fmt(stats?.pending_amount)} icon={CurrencyInr} testId="kpi-pending" />
        <Kpi label="Orders in Process" value={stats?.orders_process ?? 0} icon={ShoppingCart} testId="kpi-orders-process" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline text-muted-foreground">Trend</div>
              <h3 className="font-heading font-bold text-xl mt-1">Monthly Sales</h3>
            </div>
            <ChartLine size={20} weight="duotone" className="text-primary" />
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={stats?.monthly_sales || []}>
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} formatter={(v) => fmt(v)} />
              <Line type="monotone" dataKey="sales" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ fill: 'hsl(var(--primary))', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <div className="overline text-muted-foreground mb-1">Pipeline</div>
          <h3 className="font-heading font-bold text-xl mb-6">Lead Stages</h3>
          <div className="space-y-3">
            {Object.entries(stats?.pipeline || {}).map(([stage, count]) => (
              <div key={stage} className="flex items-center justify-between text-sm">
                <span className="capitalize text-muted-foreground">{stage.replace("_"," ")}</span>
                <span className="font-heading font-bold">{count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline text-muted-foreground">Team</div>
            <h3 className="font-heading font-bold text-xl mt-1">Employee Performance</h3>
          </div>
          <Users size={20} weight="duotone" className="text-primary" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-muted-foreground tracking-wider border-b border-border">
                <th className="py-3 pr-4">Name</th>
                <th className="py-3 pr-4">Role</th>
                <th className="py-3 pr-4">Leads</th>
                <th className="py-3 pr-4">Won</th>
                <th className="py-3">Conversion</th>
              </tr>
            </thead>
            <tbody>
              {perf.map((p, i) => (
                <tr key={i} className="border-b border-border/40 hover:bg-secondary/40">
                  <td className="py-3 pr-4 font-medium">{p.name}</td>
                  <td className="py-3 pr-4 text-muted-foreground capitalize">{p.role?.replace("_"," ")}</td>
                  <td className="py-3 pr-4">{p.leads}</td>
                  <td className="py-3 pr-4">{p.won}</td>
                  <td className="py-3">{p.conversion}%</td>
                </tr>
              ))}
              {!perf.length && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No team data</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
